import type { EmployeeField, PeriodType, SegmentRule } from "#shared/segmentsTypes.ts";
import { useCurrentLanguage, useI18nextInstance } from "@squide/i18next";
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import type { Translate } from "@packages/core-client/i18n/translate.ts";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { useSegmentsClient } from "../../api/segmentsQuery.ts";
import { toRuleCondition, valueArity, type RuleCondition } from "./ruleConditions.ts";
import { formatRuleDate, parseRuleDate } from "./ruleValues.ts";

export interface RuleSummary {
    rule: SegmentRule;
    id: string;
    fieldName: string | undefined;
    label: string | undefined;
    isLoading: boolean;
    isError: boolean;
}

function ruleId(rule: SegmentRule): string {
    return `${rule.propertyId}:${rule.operator}:${rule.values.join(" ")}`;
}

function formatRuleLabel(t: Translate, language: string, fieldName: string, condition: RuleCondition, values: string[], periodType?: PeriodType): string {
    if (condition === "is") {
        return `${fieldName}: ${values[0] ?? ""}`;
    }

    if (condition === "isBetween") {
        return `${fieldName} ${t("conditions.isBetween")} ${values[0] ?? ""} ${t("rangeConnector")} ${values[1] ?? ""}`;
    }

    const arity = valueArity(condition);

    if (arity === "period") {
        const unit = periodType ? t(`value.periodTypes.${periodType}`) : "";

        return `${fieldName} ${t(`conditions.${condition}`)} ${values[0] ?? ""} ${unit}`.trim();
    }

    if (arity === "periodRange") {
        const unit = periodType ? t(`value.periodTypes.${periodType}`) : "";

        return `${fieldName} ${t(`conditions.${condition}`)} ${values[0] ?? ""} ${t("rangeConnector")} ${values[1] ?? ""} ${unit}`.trim();
    }

    const joined = new Intl.ListFormat(language, { type: "conjunction" }).format(values);

    return `${fieldName} ${t(`conditions.${condition}`)} ${joined}`;
}

export function useRuleSummaries(rules: SegmentRule[], fields: EmployeeField[]): RuleSummary[] {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const language = useCurrentLanguage();
    const session = useSession();
    const { queryUtils } = useSegmentsClient();
    const orgId = session?.organizationId ?? "";

    const fieldsById = useMemo(() => new Map(fields.map(field => [field.id, field])), [fields]);

    const optionFieldIds = useMemo(() => {
        const ids = new Set<string>();

        for (const rule of rules) {
            if (fieldsById.get(rule.propertyId)?.type === "option") {
                ids.add(rule.propertyId);
            }
        }

        return [...ids];
    }, [rules, fieldsById]);

    const optionQueries = useQueries({
        queries: optionFieldIds.map(fieldId => createQueryOptions(queryUtils.listEmployeeFieldValues.queryOptions({ input: { orgId, fieldId } }), { throwOnError: false }))
    });

    const optionLabelsByFieldId = useMemo(() => {
        const map = new Map<string, Map<string, string>>();

        optionFieldIds.forEach((fieldId, index) => {
            map.set(fieldId, new Map((optionQueries[index]?.data?.items ?? []).map(item => [item.value, item.label])));
        });

        return map;
    }, [optionFieldIds, optionQueries]);

    const personIds = useMemo(() => {
        const ids = new Set<string>();

        for (const rule of rules) {
            const type = fieldsById.get(rule.propertyId)?.type;

            if (type === "person" || type === "personReference") {
                rule.values.forEach(value => ids.add(value));
            }
        }

        return [...ids];
    }, [rules, fieldsById]);

    const {
        data: people,
        isPending: arePeoplePending,
        isError: havePeopleFailed
    } = useQuery(
        createQueryOptions(queryUtils.searchRulePeople.queryOptions({ input: { orgId, ids: personIds } }), {
            throwOnError: false,
            enabled: personIds.length > 0
        })
    );

    const peopleById = useMemo(() => new Map((people?.items ?? []).map(person => [person.id, person])), [people]);

    return rules.map(rule => {
        const id = ruleId(rule);
        const field = fieldsById.get(rule.propertyId);

        if (!field) {
            return { rule, id, fieldName: undefined, label: undefined, isLoading: false, isError: true };
        }

        const condition = toRuleCondition(rule.operator, field.type);

        if (!condition) {
            return { rule, id, fieldName: field.displayName, label: undefined, isLoading: false, isError: true };
        }

        if (field.type === "option") {
            const query = optionQueries[optionFieldIds.indexOf(field.id)];

            if (query?.isPending) {
                return { rule, id, fieldName: field.displayName, label: undefined, isLoading: true, isError: false };
            }
            if (query?.isError) {
                return { rule, id, fieldName: field.displayName, label: undefined, isLoading: false, isError: true };
            }

            const labels = optionLabelsByFieldId.get(field.id);
            const values = rule.values.map(value => labels?.get(value) ?? value);

            return { rule, id, fieldName: field.displayName, label: formatRuleLabel(t, language, field.displayName, condition, values), isLoading: false, isError: false };
        }

        if (field.type === "person" || field.type === "personReference") {
            if (arePeoplePending) {
                return { rule, id, fieldName: field.displayName, label: undefined, isLoading: true, isError: false };
            }
            if (havePeopleFailed) {
                return { rule, id, fieldName: field.displayName, label: undefined, isLoading: false, isError: true };
            }

            const values = rule.values.map(value => {
                const person = peopleById.get(value);

                if (!person) {
                    return value;
                }

                return person.isDeactivated ? t("value.personDeactivatedLabel", { name: person.displayName }) : person.displayName;
            });

            return { rule, id, fieldName: field.displayName, label: formatRuleLabel(t, language, field.displayName, condition, values), isLoading: false, isError: false };
        }

        if (field.type === "date") {
            const values = rule.values.map(value => {
                const parsed = parseRuleDate(value);

                return parsed ? formatRuleDate(parsed) : value;
            });

            return { rule, id, fieldName: field.displayName, label: formatRuleLabel(t, language, field.displayName, condition, values, rule.periodType), isLoading: false, isError: false };
        }

        return { rule, id, fieldName: field.displayName, label: formatRuleLabel(t, language, field.displayName, condition, rule.values), isLoading: false, isError: false };
    });
}
